import React, { useCallback, useMemo, memo } from 'react';
import { Table, Space, Card, Tooltip, Popconfirm } from 'antd';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { DeleteIcon, EditIcon } from 'icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';
import useCarousels from '../useCarousels';
import { CarouselsFilters } from './CarouselsFilters';
import { EmptyState } from './EmptyState';
import { Button } from 'components';

export const CarouselsList = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pages = useRecoilValue(getPage);

  const {
    carousels,
    loading,
    total,
    currentPage,
    pageSize,
    filters,
    sorting,
    loadingCompanies,
    updateFilters,
    handleTableChange,
    deleteCarousel,
    shoppingCenters,
  } = useCarousels();
  const handleDelete = useCallback(
    async (id) => {
      try {
        const success = await deleteCarousel(id);
        if (success) {
          window.location.reload();
        }
      } catch (err) {
        console.error('Error deleting carousel:', err);
      }
    },
    [deleteCarousel],
  );

  const handleFiltersChange = useCallback(
    (newFilters) => {
      updateFilters(newFilters);
    },
    [updateFilters],
  );

  const columns = useMemo(
    () => [
      {
        title: <div style={{ textAlign: 'center' }}>{t('Outlet Village')}</div>,
        dataIndex: 'shopping_center.title',
        key: 'shopping_center.title',
        sorter: () => {},
        sortOrder:
          sorting.field === 'shopping_center.title'
            ? sorting.order === 'asc'
              ? 'ascend'
              : sorting.order === 'desc'
                ? 'descend'
                : null
            : null,
        render: (_, record) => (
          <span style={{ fontWeight: 500 }}>{record.shopping_center?.title}</span>
        ),
        align: 'center',
      },
      {
        title: <div style={{ textAlign: 'center' }}>{t('Insegna')}</div>,
        dataIndex: 'company.title',
        key: 'company.title',
        sorter: () => {},
        sortOrder:
          sorting.field === 'company.title'
            ? sorting.order === 'asc'
              ? 'ascend'
              : sorting.order === 'desc'
                ? 'descend'
                : null
            : null,
        render: (_, record) => (
          <span style={{ fontWeight: 500 }}>{record.company?.title}</span>
        ),
        ellipsis: true,
        align: 'center',
      },
      {
        title: <div style={{ textAlign: 'center' }}>{t('Azioni')}</div>,
        key: 'actions',
        width: 180,
        align: 'center',
        render: (_, record) => {
          if (!record || !record.id) {
            return <span>{t('Nessuna azione disponibile')}</span>;
          }
          return (
            <Space size='small'>
              {(pages?.ability?.can_read !== false ||
                record.can_read !== false) && (
                <Tooltip title={t('Visualizza')}>
                  <Button
                    type='secondary'
                    icon={<EyeOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/website/carousels/${record.id}`);
                    }}
                  />
                </Tooltip>
              )}
              {record.can_update !== false && (
                <Tooltip title={t('Modifica')}>
                  <Button
                    type='secondary'
                    icon={<EditIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/website/carousels/edit/${record.id}`);
                    }}
                  />
                </Tooltip>
              )}
              {record.can_destroy !== false && (
                <Popconfirm
                  title={t('Sei sicuro di voler cancellare?')}
                  okText={t('Si, cancella')}
                  cancelText={t('No, lascia')}
                  onConfirm={() => handleDelete(record.id)}
                >
                  <span onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={t('Elimina')}>
                      <Button type='red' icon={<DeleteIcon />} />
                    </Tooltip>
                  </span>
                </Popconfirm>
              )}
            </Space>
          );
        },
      },
    ],
    [t, navigate, handleDelete, pages?.ability?.can_read, sorting],
  );

  if (!loading && carousels.length === 0) {
    return (
      <Card
        title={
          <span style={{ fontSize: '24px', fontWeight: '600' }}>
            {t('Slider loghi HP')}
          </span>
        }
        extra={
          <Button
            type='primary'
            icon={<PlusOutlined />}
            onClick={() => navigate('/website/carousels/create')}
            style={{
              marginBottom: 12,
              width: '140px',
              height: 48,
              backgroundColor: '#002637',
              borderColor: '#002637',
            }}
          >
            {t('Nuovo')}
          </Button>
        }
      >
        <CarouselsFilters
          onFiltersChange={handleFiltersChange}
          shoppingCenters={shoppingCenters}
          loadingCompanies={loadingCompanies}
          filters={filters}
        />
        <EmptyState />
      </Card>
    );
  }

  return (
    <Card
      title={
        <span style={{ fontSize: '24px', fontWeight: '600' }}>
          {t('Slider loghi HP')}
        </span>
      }
      extra={
        <Button
          type='primary'
          icon={<PlusOutlined />}
          onClick={() => navigate('/website/carousels/create')}
          style={{
            marginBottom: 12,
            width: '140px',
            height: 48,
            backgroundColor: '#002637',
            borderColor: '#002637',
          }}
        >
          {t('Nuovo')}
        </Button>
      }
    >
      <CarouselsFilters
        onFiltersChange={handleFiltersChange}
        shoppingCenters={shoppingCenters}
        loadingCompanies={loadingCompanies}
        filters={filters}
      />
      <Table
        columns={columns}
        dataSource={carousels}
        loading={loading}
        rowKey='id'
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: false,
        }}
        onChange={handleTableChange}
        rowClassName={() => 'carousel-row'}
        style={{ marginTop: 8 }}
        locale={{
          emptyText: t('Nessun dato trovato'),
        }}
      />
      <style>{`
        .carousel-row:hover {
          background: #f5f7fa !important;
        }
      `}</style>
    </Card>
  );
});
